import { supabase, isSupabaseConfigured } from './supabaseClient'

/* Single data-access layer for products.
 * The catalog lives only in Supabase, where row-level security returns it to
 * approved researcher accounts and admins (compliance gate). There is no
 * bundled fallback catalog: product names and prices must never ship in the
 * public site code. Components call these functions rather than Supabase. */

/** Storefront category filter options. */
export const categories = ['All', 'GLP', 'Peptides', 'Compounds']

export async function fetchProducts() {
  if (isSupabaseConfigured) {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .order('sort_order', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: false })
    if (error) {
      console.warn('Could not load the catalog:', error.message)
      return []
    }
    return data || []
  }
  return []
}

export async function fetchFeaturedProducts() {
  const all = await fetchProducts()
  return all.filter((p) => p.featured)
}

/* ---------------------------------------------------------------------------
 * Admin write operations. These require Supabase + a signed-in admin; row-level
 * security enforces that on the server. They throw on error so the admin UI can
 * surface it (no silent local fallback here).
 * ------------------------------------------------------------------------- */

function requireSupabase() {
  if (!isSupabaseConfigured) {
    throw new Error('Supabase is not configured. Add your keys to .env.')
  }
}

/** Admin catalog listing — in the manual display order (same as storefront). */
export async function adminListProducts() {
  requireSupabase()
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .order('sort_order', { ascending: true, nullsFirst: false })
    .order('created_at', { ascending: false })
  if (error) throw error
  return data
}

export async function createProduct(product) {
  requireSupabase()
  // Append new products to the end of the manual order.
  let sort_order = product.sort_order
  if (sort_order == null) {
    const { data: top } = await supabase
      .from('products')
      .select('sort_order')
      .order('sort_order', { ascending: false, nullsFirst: false })
      .limit(1)
    sort_order = (top?.[0]?.sort_order ?? 0) + 10
  }
  const { data, error } = await supabase
    .from('products')
    .insert({ ...product, sort_order })
    .select()
    .single()
  if (error) throw error
  return data
}

/** Persist a new display order. `ordered` is the full list of products in the
 *  desired order; each gets sort_order 10, 20, 30, … */
export async function reorderProducts(ordered) {
  requireSupabase()
  const updates = ordered.map((p, i) =>
    supabase.from('products').update({ sort_order: (i + 1) * 10 }).eq('id', p.id)
  )
  const results = await Promise.all(updates)
  const firstErr = results.find((r) => r.error)?.error
  if (firstErr) throw firstErr
}

export async function updateProduct(id, patch) {
  requireSupabase()
  const { data, error } = await supabase
    .from('products')
    .update(patch)
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data
}

export async function deleteProduct(id) {
  requireSupabase()
  const { error } = await supabase.from('products').delete().eq('id', id)
  if (error) throw error
}

/* Image upload requirements, surfaced in the admin UI. Product photos display
 * inside a 1:1 square that scales on mobile and desktop, so square uploads with
 * the vial centered look best. */
export const IMAGE_RULES = {
  accept: ['image/jpeg', 'image/png', 'image/webp'],
  acceptAttr: 'image/jpeg,image/png,image/webp',
  extensions: 'JPG, PNG, or WebP',
  ratioLabel: '1:1 square',
  minSize: 800, // px, square
  recommended: '1000 × 1000px', // ideal
  maxDimension: 1400, // px — larger than this is unnecessary
  maxBytes: 3 * 1024 * 1024, // 3 MB
  maxLabel: '3 MB',
}

/** Uploads a product image to Supabase Storage and returns its public URL.
 *  Validates type and size first; throws a friendly error if they fail. */
export async function uploadProductImage(file, slug = 'item') {
  requireSupabase()

  if (!IMAGE_RULES.accept.includes(file.type)) {
    throw new Error(`Unsupported file type. Use ${IMAGE_RULES.extensions}.`)
  }
  if (file.size > IMAGE_RULES.maxBytes) {
    throw new Error(`Image is too large. Keep it under ${IMAGE_RULES.maxLabel}.`)
  }

  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase()
  const safeSlug = slug.replace(/[^a-z0-9-]/gi, '').toLowerCase() || 'item'
  const path = `products/${safeSlug}-${Date.now()}.${ext}`

  const { error } = await supabase.storage
    .from('product-images')
    .upload(path, file, { cacheControl: '3600', upsert: true })
  if (error) throw error

  const { data } = supabase.storage.from('product-images').getPublicUrl(path)
  return data.publicUrl
}
